/*
 * Login.tsx
 *
 * page for "Logging in" by entering PIN number.
 *
 * Created by Nick Perry on 9/2/22.
 *
 * dha-login
 *
 * Copyright © 2009-2022 United States Government as represented by
 * the Program Manager of the DHA: Web and Mobile Technology Program Management Office.
 * All Rights Reserved.
 *
 * Copyright © 2009-2022 Contributors. All Rights Reserved.
 *
 * THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 * REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 * COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 * AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 * THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 * INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 * REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 * DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 * HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 * RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 * Government Agency: DHA: Web and Mobile Technology Program Management Office
 * Government Agency Original Software Designation: dha-login
 * Government Agency Original Software Title: dha-login
 * User Registration Requested. Please send email
 * with your contact information to: robert.a.kayl.civ@mail.mil
 * Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 */

import type { Dispatch, SetStateAction, KeyboardEvent } from 'react';
import { useContext } from 'react';

import { Dialog } from '@headlessui/react';
import { decrypt } from 'dha-encryption';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import * as yup from 'yup';

import { themeAtom } from '../../atoms/themeAtom';
import { CustomInput } from '../../components/CustomInput';
import type { IPinResetStatusState } from '../../controllers/LoginController';
import { AuthContext } from '../../providers/AuthProvider';
import { LoginDatabaseContext } from '../../providers/LoginDatabaseProvider';

// Dispatch and SetStateAction are types from react that are necessary for useState's functional update syntax, aka setState((prevstate) => {})
interface ILoginProps {
  setPinResetStatus: Dispatch<SetStateAction<IPinResetStatusState>>;
}

const Login = ({ setPinResetStatus }: ILoginProps) => {
  const { username, setAuthenticated } = useContext(AuthContext);
  const { DHA_Key, database } = useContext(LoginDatabaseContext);
  const theme = useAtomValue(themeAtom);

  const validationSchema = yup.object({
    pin: yup
      .string()
      .required('Enter a valid pin')
      .min(6, 'Pin must be at least 6 characters')
      .matches(new RegExp('^\\d+$'), 'Pin must only contain numbers')
      .trim(),
  });

  const onForget = () => {
    // changes state in LoginController to render PinReset page
    setPinResetStatus((prevState: IPinResetStatusState) => {
      return {
        ...prevState,
        answeringSecurityQuestions: true,
      };
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Enter' || event.code === 'Space') {
      event.currentTarget.click();
    }
  };

  return (
    <Dialog
      open={true}
      as="div"
      className="relative z-10"
      onClose={(boolie) => {
        if (boolie) console.log(''); // this is the peak of programming & human ingenuity.
      }}
    >
      <div className={`fixed inset-0 ${theme.modalBackground}`} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Dialog.Panel
            className={`h-full max-h-fit w-full overflow-hidden rounded-2xl ${theme.cardColor} p-6 text-center align-middle shadow-xl transition-all md:max-w-sm`}
          >
            <Dialog.Title as="h3" className={`text-lg font-medium leading-6 ${theme.textColor}`}>
              Welcome back, {username}!
            </Dialog.Title>
            <div className="mt-2">
              <p className={`text-sm ${theme.textColor}`}>Please enter your PIN</p>
            </div>

            <Formik
              initialValues={{
                pin: '',
              }}
              onSubmit={(enteredValues, { setErrors }) => {
                // Check if pin is valid
                // Cache pin with ref
                if (database !== null) {
                  // function asynchronously decrypts encrypted data stored in indexDB
                  const loginWithPin = async () => {
                    // necessary check due to TypeScript restrictions
                    if (DHA_Key === undefined) {
                      return;
                    }

                    try {
                      const encryptedUser = await database.userProfile.get('primaryUser');
                      if (encryptedUser !== undefined) {
                        const decryptedUserString = await decrypt(
                          encryptedUser.userProfile,
                          DHA_Key
                        );
                        const decryptedUser = JSON.parse(decryptedUserString);

                        if (enteredValues.pin === decryptedUser.pin) {
                          setAuthenticated(true);
                        } else {
                          setErrors({ pin: 'Incorrect PIN, please try again.' });
                        }
                      }
                    } catch (err) {
                      console.log(err);
                    }
                  };
                  loginWithPin().catch((e) => console.error(e));
                }
              }}
              validationSchema={validationSchema}
            >
              {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit}>
                  <div id="form-area" className="h-28">
                    <CustomInput
                      type="password"
                      name="pin"
                      value={formikProps.values.pin}
                      tailwindClasses="mt-8"
                      isError={!!formikProps.errors.pin}
                      isTouched={formikProps.touched.pin}
                      onChange={formikProps.handleChange}
                      maxLength={6}
                      placeholder="enter PIN"
                    />

                    {formikProps.errors.pin && formikProps.touched.pin ? (
                      <div className="text-base font-medium text-rose-500" id="feedback">
                        {formikProps.errors.pin}
                      </div>
                    ) : null}
                  </div>

                  <div
                    onClick={onForget}
                    onKeyDown={handleKeyDown}
                    className={`${theme.secondaryTextColor} underline hover:cursor-pointer`}
                    role="button"
                    tabIndex={0}
                  >
                    forgot PIN?
                  </div>

                  <div className="">
                    <button
                      type="submit"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium ${theme.buttonShadow} ${theme.buttonColor} ${theme.buttonTextColor} hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 mt-5`}
                    >
                      Login
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default Login;
